import { FunctionComponent, useCallback, useRef, useEffect } from 'react'
import useLivestreamStoryContext from './useLivestreamStoryContext'
import config from '@config'
import { ChapterAPIResponse, APIChapter, Chapter } from './types'
import {
  calculateChapterTimelinePosition,
  normalizePlayerCurrentTimeValue,
  hasChaptersPlaceholder,
} from './utils'
import { formatDuration } from '@widgets/Video/utils'
import { useQuery } from '@tanstack/react-query'
import useIsSwissGeolocation from '@hooks/useIsSwissGeolocation'
import {
  getJWPlayer,
  playerSeekTo,
  setPlayerGeoBlockedError,
} from '@utils/videoPlayer'

const {
  chaptersApiUrl,
  story: { dvrWindow, pollingIntervalInMilliseconds },
} = config.video

const formatChapters = (chapters: APIChapter[]): Chapter[] =>
  chapters.reverse().map((chapter: APIChapter, index: number) => ({
    ...chapter,
    duration: formatDuration(chapter?.length),
    isLive: index === chapters?.length - 1, // the last chapter should be live
  }))

const getChapters = async (): Promise<Chapter[]> => {
  const res = await fetch(`${chaptersApiUrl}?dvr=${dvrWindow}`)
  const data: ChapterAPIResponse = await res.json()

  return data ? formatChapters(data) : []
}

const LivestreamStory: FunctionComponent = () => {
  const {
    chapters,
    setChapters,
    activeChapter,
    setActiveChapter,
    timelinePointers,
    widgetId,
  } = useLivestreamStoryContext()

  const isGeoBlockedError = useRef<boolean>(false)

  const { data: chaptersData } = useQuery<Chapter[]>({
    queryKey: ['chapters'],
    queryFn: () => getChapters(),
    refetchInterval: pollingIntervalInMilliseconds,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  })

  const isSwissGeolocation = useIsSwissGeolocation()

  useEffect(() => {
    if (chaptersData) {
      setChapters(chaptersData)
    }
  }, [chaptersData, setChapters])

  const seekTo = useCallback(
    (seconds: number) => {
      const jwPlayer = getJWPlayer(widgetId)

      if (jwPlayer) {
        playerSeekTo(jwPlayer, seconds)
      }
    },
    [widgetId]
  )

  const seekToChapterPosition = useCallback(
    (chapterData?: Chapter) => {
      if (chapterData) {
        const jwPlayer = getJWPlayer(widgetId)

        if (!jwPlayer || !chapterData.timestamp) {
          return
        }

        const playerNewCurrentTimeValue = calculateChapterTimelinePosition(
          chapterData.timestamp,
          timelinePointers
        )

        const seekToPosition = normalizePlayerCurrentTimeValue(
          playerNewCurrentTimeValue,
          jwPlayer
        )

        seekTo(seekToPosition)
      }
    },
    [widgetId, seekTo, timelinePointers]
  )

  const setLastChapterAsActive = useCallback(() => {
    if (chapters?.length && !hasChaptersPlaceholder(chapters)) {
      const lastChapter = chapters[chapters.length - 1]

      if (lastChapter) {
        setActiveChapter(lastChapter)
      }
    }
  }, [chapters, setActiveChapter])

  const setGeoBlockedError = useCallback(
    (showError: boolean) => {
      const jwPlayer = getJWPlayer(widgetId)

      if (jwPlayer) {
        setPlayerGeoBlockedError(jwPlayer, showError)

        if (showError) {
          jwPlayer.pause()
        } else {
          seekToChapterPosition(activeChapter)
          jwPlayer.play()
        }
      }

      isGeoBlockedError.current = showError
    },
    [activeChapter, widgetId, seekToChapterPosition]
  )

  // set last chapter as initial value for activeChapter
  useEffect(() => {
    if (!activeChapter?.id) {
      setLastChapterAsActive()
    }
  }, [activeChapter?.id, setLastChapterAsActive])

  useEffect(() => {
    if (!isSwissGeolocation && activeChapter?.id) {
      if (activeChapter?.is_geoblocked) {
        setGeoBlockedError(true)
      } else {
        if (isGeoBlockedError.current) {
          setGeoBlockedError(false)
        }
      }
    }
  }, [
    isSwissGeolocation,
    activeChapter?.id,
    activeChapter?.is_geoblocked,
    setGeoBlockedError,
  ])

  return null
}

export default LivestreamStory
